import { ActionTree } from 'vuex';
import { EFieldSource } from '../types/enums/field-source';
import { ISelectedPageFieldIds } from '../types/selected-page-field-ids';
import {
  EFieldAction,
  EFieldGetter,
  EFieldMutation,
  state as defaultState,
} from './field.constants';
import { IFieldState } from './field.types';
import { dealroomNamespace, EDealroomGetter } from '@/dealroom/store/dealroom.constants';
import { EAlignFieldType } from '@/dealroom/fields/context-menu/types/enums/align-field-type';
import { createDefaultField } from '@/dealroom/fields/helpers/field-default.helper';
import {
  calculateFieldContainerPosition,
  calculateFieldOffset,
  correctFieldPosition,
} from '@/dealroom/fields/helpers/field-placement.helper';
import service from '@/dealroom/fields/services/field.service';
import { EFieldType } from '@/dealroom/fields/types/enums/field-type';
import { EMergeValue } from '@/dealroom/fields/types/enums/merge-value';
import { IField } from '@/dealroom/fields/types/field';
import { IFieldPosition } from '@/dealroom/fields/types/field-position';
import { translate } from '@/helpers/translation.helper';
import { RootState } from '@/store/store';
import { namespace as alertNamespace } from '@/store/stores/alert.store';
import { IDocument } from '@/types/Document';
import { IDocumentPage } from '@/types/document-page';
import { IRecipient } from '@/types/Recipient';
import { IUser } from '@/types/User';
import { EnumDocumentType } from '@/enums/DocumentType';
import { MergeTagHelper } from '@/helpers/merge-tag.helper';
import { FieldHelper } from '@/dealroom/fields/helpers/field.helper';
import { EDocumentPageType } from '@/enums/document-page-type';

export const actions: ActionTree<IFieldState, RootState> = {
  async [EFieldAction.Load]({ dispatch, commit }, documentId: string) {
    commit(EFieldMutation.SetDocumentId, documentId);
    commit(EFieldMutation.SetLoading, true);
    try {
      const fields: IField[] = await service.loadFields(documentId);
      const mappedFields: IField[] = fields.map((field: IField) =>
        field.type === EFieldType.Merge
          ? {
              ...field,
              displayProperty: MergeTagHelper.getMergeDisplayProperty(field.value),
            }
          : field
      );
      dispatch(EFieldAction.LoadSuccess, mappedFields);
    } catch (err) {
      dispatch(EFieldAction.LoadFailed);
    }
  },
  [EFieldAction.LoadSuccess]({ commit }, fields: IField[]) {
    commit(EFieldMutation.Set, fields);
    commit(EFieldMutation.SetLoading, false);
  },
  [EFieldAction.LoadFailed]({ commit }) {
    commit(EFieldMutation.SetLoading, false);
  },
  async [EFieldAction.Create](
    { commit, dispatch, state, getters, rootGetters },
    {
      documentPage,
      documentId,
      documentType,
      type,
      mergeValue,
      sender,
      recipient,
      top,
      left,
    }: {
      documentPage: IDocumentPage;
      documentId: string;
      documentType: string;
      type: EFieldType;
      mergeValue?: EMergeValue;
      sender?: IUser;
      recipient?: IRecipient;
      top?: number;
      left?: number;
    }
  ) {
    if (documentPage.pageType !== EDocumentPageType.DocumentContent) {
      dispatch(EFieldAction.CreateFailed, documentPage.pageType);
      return;
    }
    const lastUpdatedFieldHasSamePage: boolean =
      getters[EFieldGetter.LastUpdatedField] &&
      documentPage.id === getters[EFieldGetter.LastUpdatedField].pageId;
    const field: IField = createDefaultField(
      documentPage,
      documentId,
      type,
      state.pageWidth,
      rootGetters[`${dealroomNamespace}/${EDealroomGetter.ConnectFirstRecipient}`],
      mergeValue,
      sender,
      recipient && recipient.id,
      top,
      left,
      lastUpdatedFieldHasSamePage ? getters[EFieldGetter.LastUpdatedField] : null
    );
    field.position = calculateFieldOffset(state.fields, field);
    field.position = correctFieldPosition(field.position, field.pageId, state.pageWidth);
    field.isLoading = true;
    field.id = FieldHelper.getTempId();
    field.isEditable = true;
    // If the field is created from a template the user should not be able to alter the field when creating a document based on the template
    if (documentType === EnumDocumentType.Template) {
      field.isEditable = false;
    }

    commit(EFieldMutation.Add, [field]);
    try {
      const id: string = await service.createField(field);
      const savedField: IField = { ...field, id, isLoading: false };
      commit(EFieldMutation.UpdateDone, {
        field: savedField,
        tempId: field.id,
      });
      dispatch(EFieldAction.CreateSuccess, savedField);
    } catch {
      commit(EFieldMutation.Delete, [field.id]);
      dispatch(EFieldAction.CreateFailed);
    }
  },
  [EFieldAction.CreateFailed]({ dispatch }, pageType?: EDocumentPageType) {
    let errorMessage = 'could-not-create-field';
    if (pageType && pageType !== EDocumentPageType.DocumentContent) {
      errorMessage = translate('can-not-create-field-on');
      switch (pageType) {
        case EDocumentPageType.ContentLink:
          errorMessage += ` ${translate('content-link').toLowerCase()}`;
          break;
        case EDocumentPageType.Editor:
          errorMessage += ` ${translate('editor').toLowerCase()}`;
          break;
        case EDocumentPageType.Video:
          errorMessage += ` ${translate('video').toLowerCase()}`;
          break;
        default:
          errorMessage += ` ${translate('this-block-type').toLowerCase()}`;
          break;
      }
    }
    dispatch(`${alertNamespace}/showDangerAlert`, errorMessage, {
      root: true,
    });
  },
  [EFieldAction.CreateSuccess]({ dispatch }, field: IField) {
    if (field.type === EFieldType.Merge) {
      field = {
        ...field,
        displayProperty: MergeTagHelper.getMergeDisplayProperty(field.value),
      };
    }
    if (field.type === EFieldType.Initials || field.type === EFieldType.Signature) {
      const payload: Partial<IDocument> = {
        isSigning: 1,
      };
      if (field.source === EFieldSource.Sender) {
        payload.isSelfsign = 1;
      }
      if (field.type === EFieldType.Signature) {
        payload.isSigningBiometric = 1;
      }
      if (field.type === EFieldType.Initials) {
        payload.isSigningInitials = 1;
      }
      dispatch(`${dealroomNamespace}/updateDocument`, payload, { root: true });
    }
    dispatch(EFieldAction.SetLastUpdatedFieldId, [field]);
  },
  async [EFieldAction.Update]({ commit, state, dispatch }, fields: IField[]) {
    const previousFields: IField[] = state.fields;
    const updatedFields: IField[] = previousFields.map((stateField: IField) =>
      fields.some((field: IField) => field.id === stateField.id)
        ? fields.find((field: IField) => field.id === stateField.id)
        : stateField
    );
    dispatch(EFieldAction.SetLastUpdatedFieldId, fields);
    commit(EFieldMutation.Set, updatedFields);
    try {
      await service.updateFields(fields);
    } catch {
      dispatch(EFieldAction.UpdateFailed, previousFields);
    }
  },
  [EFieldAction.UpdateFailed]({ commit, dispatch }, previousField: IField) {
    commit(EFieldMutation.Update, previousField);
    dispatch(`${alertNamespace}/showDangerAlert`, translate('could-not-update-field'), {
      root: true,
    });
  },
  [EFieldAction.Select]({ commit }, field: IField) {
    commit(EFieldMutation.Select, field);
  },
  [EFieldAction.Deselect]({ commit }, field: IField) {
    commit(EFieldMutation.Deselect, field);
  },
  [EFieldAction.Tab]({ commit, state }, pages: IDocumentPage[]) {
    const { fieldIds } = state.selectedPageFieldIds;
    const nextField: IField = FieldHelper.getNextTabField(state.fields, fieldIds, pages);
    commit(EFieldMutation.SetSelected, [nextField]);
    commit(EFieldMutation.SetTabFieldId, nextField.id);
  },
  async [EFieldAction.Delete]({ commit, dispatch, state }) {
    const fieldsToBeDeleted: IField[] = state.fields.filter((field: IField) =>
      state.selectedPageFieldIds.fieldIds.some((id: string) => id === field.id)
    );
    commit(EFieldMutation.Delete, state.selectedPageFieldIds.fieldIds);
    try {
      await service.deleteFields(state.selectedPageFieldIds.fieldIds);
    } catch {
      dispatch(EFieldAction.DeleteFailed, fieldsToBeDeleted);
    }
    commit(EFieldMutation.DeselectAll);
  },
  [EFieldAction.DeleteFailed]({ commit, dispatch }, fields: IField[]) {
    commit(EFieldMutation.Add, fields);
    dispatch(`${alertNamespace}/showDangerAlert`, translate('could-not-delete-field'), {
      root: true,
    });
  },
  async [EFieldAction.DeleteSignatureFields]({ commit, state }) {
    const fieldsToBeDeleted: IField[] = FieldHelper.getSignatureFields(state.fields);
    const fieldIds: string[] = fieldsToBeDeleted.map((field: IField) => field.id);
    commit(EFieldMutation.Delete, fieldIds);
    try {
      await service.deleteFields(fieldIds);
    } catch {
      this.dispatch(EFieldAction.DeleteFailed, fieldsToBeDeleted);
    }
  },
  [EFieldAction.DeselectAll]({ commit }) {
    commit(EFieldMutation.DeselectAll);
  },
  async [EFieldAction.Duplicate]({ commit, dispatch, state }) {
    try {
      const fieldsToCopy: IField[] = state.fields
        .filter((field: IField) =>
          state.selectedPageFieldIds.fieldIds.find((id: string) => id === field.id)
        )
        .map((field: IField) => {
          const fieldToCopy: IField = { ...field };
          delete fieldToCopy.id;
          const duplicatePosition: IFieldPosition = FieldHelper.getDuplicateFieldPosition(field);

          return {
            ...fieldToCopy,
            position: correctFieldPosition(duplicatePosition, field.pageId, state.pageWidth),
          };
        });
      const copiedFields: IField[] = await service.createFields(fieldsToCopy);
      commit(EFieldMutation.Add, copiedFields);
      commit(EFieldMutation.SetSelected, copiedFields);
      dispatch(EFieldAction.SetLastUpdatedFieldId, copiedFields);
    } catch (e) {
      dispatch(EFieldAction.DuplicateFailed);
    }
  },
  [EFieldAction.DuplicateFailed]({ dispatch }) {
    dispatch(`${alertNamespace}/showDangerAlert`, translate('could-not-copy-field'), {
      root: true,
    });
  },
  [EFieldAction.UpdatePageWidth]({ commit }, pageWidth: number) {
    commit(EFieldMutation.SetPageWidth, pageWidth);
  },
  [EFieldAction.Align]({ state, dispatch }, actionType: EAlignFieldType) {
    const fields: IField[] = state.fields.filter((field: IField) =>
      state.selectedPageFieldIds.fieldIds.find((id: string) => id === field.id)
    );
    try {
      const fieldContainerPosition: IFieldPosition = calculateFieldContainerPosition(fields);
      const updatedFields: IField[] = FieldHelper.getAlignedFields(
        fields,
        actionType,
        fieldContainerPosition
      );
      dispatch(EFieldAction.Update, updatedFields);
    } catch (e) {
      dispatch(EFieldAction.AlignFailed);
    }
  },
  [EFieldAction.AlignFailed]({ dispatch }) {
    dispatch(`${alertNamespace}/showDangerAlert`, translate('could-not-align-fields'), {
      root: true,
    });
  },
  [EFieldAction.Move]({ dispatch, state }, pageId: string) {
    const movedFields: IField[] = state.fields
      .filter((field: IField) =>
        state.selectedPageFieldIds.fieldIds.find((id: string) => id === field.id)
      )
      .map((field: IField) => ({
        ...field,
        pageId,
        position: correctFieldPosition(field.position, pageId, state.pageWidth),
      }));

    dispatch(EFieldAction.Update, movedFields);
    dispatch(EFieldAction.DeselectAll);
  },
  [EFieldAction.ChangeTextSize]({ dispatch, state }, height: number) {
    try {
      const updatedFields: IField[] = state.fields
        .filter((field: IField) =>
          state.selectedPageFieldIds.fieldIds.find((id: string) => id === field.id)
        )
        .map((selectedField: IField) => {
          if (selectedField.position.height !== height && selectedField.type !== EFieldType.Check) {
            selectedField.position.height = height;
          }
          return selectedField;
        });
      dispatch(EFieldAction.Update, updatedFields);
    } catch (e) {
      dispatch(EFieldAction.ChangeTextSizeFailed);
    }
  },
  [EFieldAction.ChangeTextSizeFailed]({ dispatch }) {
    dispatch(`${alertNamespace}/showDangerAlert`, translate('could-not-change-field-size'), {
      root: true,
    });
  },
  [EFieldAction.Focus]({ commit }, fieldId: string) {
    commit(EFieldMutation.SetFocusedFieldId, fieldId);
  },
  async [EFieldAction.LoadSenderSignature]({ dispatch }, userId: string) {
    try {
      const signatureData: string = await service.loadUserSignature(userId);
      dispatch(EFieldAction.LoadSenderSignatureSuccess, signatureData);
    } catch (e) {
      dispatch(EFieldAction.LoadSenderSignatureFailed);
    }
  },
  [EFieldAction.LoadSenderSignatureSuccess]({ commit }, signatureData: string) {
    commit(EFieldMutation.SetSenderSignature, signatureData);
  },
  [EFieldAction.LoadSenderSignatureFailed]({ dispatch }) {
    dispatch(`${alertNamespace}/showDangerAlert`, translate('could-not-load-signature'), {
      root: true,
    });
  },
  [EFieldAction.SetLastUpdatedFieldId]({ commit }, updatedFields: IField[]) {
    const sortedUpdatedFields: IField[] = FieldHelper.sortByPositionWithoutPages(updatedFields);
    const lastUpdatedField: IField = sortedUpdatedFields[sortedUpdatedFields.length - 1];
    commit(EFieldMutation.SetLastUpdatedFieldId, lastUpdatedField.id);
  },
  [EFieldAction.SetSelectBox]({ commit }, selectedPageFieldIds: ISelectedPageFieldIds) {
    commit(EFieldMutation.SetSelectBox, selectedPageFieldIds);
  },
  [EFieldAction.EndSelectBox]({ commit, state }) {
    commit(EFieldMutation.SetSelectedPageFieldIds, state.markedPageFieldIds);
    commit(EFieldMutation.SetSelectBox, defaultState.markedPageFieldIds);
  },
};
